import { useState } from "react";
import { createPortal } from "react-dom";
import {
  Button,
  Chip,
  Icon,
  Text,
} from "@ui-component-library";
import { TailwindWrapper } from "@utils/TailwindWrapper";
import { useSubscription } from "../../../context/SubscriptionCheckoutContext";
import SubscriptionDetailsDialog from "../components/SubscriptionDetailsDialog";

const getTextForSubscription = (subscription, selectedProperties) => {
  const price = selectedProperties.reduce(
    (acum, prop) => acum + prop.plus_price,
    0
  );
  if (subscription.id === 0) {
    return {
      id: subscription.id,
      title: "Treebula Plus",
      description: `Med Treebula Plus får du <b>full kontroll</b> över din skog och <b>nya möjligheter</b> att skapa värde. Du får en <b>smart, trygg</b> och <b>aktuell</b> helhetslösning som hjälper dig att <b>maximera lönsamheten</b> och fatta <b>välgrundade beslut</b> för din fastighets framtid.`,
      featureTitle: "Som Pluskund får du bland annat:",
      featuresList: [
        "Komplett digital skogsbruksplan",
        "Fullständig tillgång till prisjämförelse på virke",
        "Skogsövervakning",
        "Fastighetsvärdering",
        "Marknadsinformation och notiser",
      ],
      price: price,
      internalName: subscription.internal_name,
    };
  }

  return;
};

const Card = ({
  id,
  title,
  description,
  price,
  totalForestArea,
  featureTitle,
  featuresList,
  onClick,
}) => {
  return (
    <div
      id={`subscription-${id}`}
      className="flex flex-col gap-2 w-full border border-[#E5E5E5] p-4 rounded-xl bg-white-50"
    >
      <div className="flex flex-col gap-2 mb-4">
        <div className="flex flex-row gap-2 items-baseline justify-between">
          <Text
            variant="text-base/leading-normal/medium"
            className="text-charcoal-500"
          >
            {title}
          </Text>
          <Chip
            tw
            label="Rekommenderas"
            size="medium"
            className="bg-[#F5F5F5]"
          />
        </div>

        <p
          className="leading-sm text-sm font-normal tracking-wide"
          dangerouslySetInnerHTML={{ __html: description }}
        />
      </div>

      <div className="flex flex-col h-full items-start mb-6">
        {totalForestArea > 0 && (
          <div className="flex flex-col items-end gap-1">
            <div className="flex flex-row gap-1 items-baseline">
              <Text
                variant="text-2xl/leading-normal/medium"
                className="text-charcoal-500"
              >
                {price}
              </Text>
              <Text
                variant="text-sm/leading-normal/normal"
                className="text-charcoal-500"
              >
                kr/år för {totalForestArea} ha
              </Text>
            </div>
          </div>
        )}
      </div>
      <div className="flex flex-col h-full items-start gap-3 mb-6">
        <Text variant="text-sm/leading-normal/medium">{featureTitle}</Text>
        {featuresList.map((feature, index) => (
          <div
            key={index}
            className="flex flex-row gap-3 items-center justify-between w-full"
          >
            <div className="flex flex-row gap-3 items-center">
              <Icon
                tw
                name="CheckSimple"
                size="medium"
                className="text-charcoal-500"
              />
              <Text
                variant="text-sm/leading-normal/normal"
                className="text-charcoal-500"
              >
                {feature}
              </Text>
            </div>
          </div>
        ))}
      </div>
      {/* commented out to prevent showing sidebar until it is fixed
      <Button
        tw
        variant="secondary"
        size="small"
        className="gap-2"
        onClick={onClick}
      >
        <Icon
          tw
          name="SparkleSimple"
          size="medium"
          className="fill-charcoal-500"
        />
        <Text
          variant="text-sm/leading-normal/medium"
          className="text-charcoal-500"
        >
          Se allt som ingår
        </Text>
      </Button> */}
    </div>
  );
};

export default function SubscriptionInfo() {
  const { subscriptionPrices, selectedProperties } = useSubscription();
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  if (!subscriptionPrices?.products) {
    return <div className="flex p-4">Laddar...</div>;
  }

  const subscription = getTextForSubscription(
    subscriptionPrices.products.plus,
    selectedProperties
  );

  if (!subscription) {
    return <div className="flex p-4">Laddar...</div>;
  }

  return (
    <>
      <div className="flex flex-col w-full">
        <div className="w-full flex flex-col gap-4">
          <div className="flex flex-col gap-4">
            <Card
              key={subscription.title}
              {...subscription}
              onClick={() => setIsDetailsOpen(true)}
            />
          </div>
        </div>
      </div>

      {createPortal(
        <TailwindWrapper>
          <SubscriptionDetailsDialog
            isOpen={isDetailsOpen}
            onClose={() => setIsDetailsOpen(false)}
            subscription={subscription}
          />
        </TailwindWrapper>,
        document.body
      )}
    </>
  );
}
